import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAdminEmailThreads,
  getAdminEmailThreadDetail,
} from "@/services/admin/adminApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const phaseVariant: Record<string, "secondary" | "outline" | "default" | "destructive"> = {
  inquiry: "outline",
  awaiting_shift: "secondary",
  shift: "default",
  change: "default",
  dormant: "secondary",
  completed: "destructive",
};

const ThreadDetail = ({ threadId }: { threadId: string }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-thread-detail", threadId],
    queryFn: () => getAdminEmailThreadDetail(threadId),
  });

  if (isLoading) {
    return <p className="text-sm text-gray-400 mt-3">Loading messages...</p>;
  }
  if (!data) return null;

  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      {data.change_intent && (
        <div className="bg-blue-50 border border-blue-100 rounded p-3">
          <p className="text-xs font-semibold text-blue-700 mb-1 uppercase tracking-wide">
            Change Intent
          </p>
          <p className="text-sm text-blue-900 leading-relaxed">{data.change_intent}</p>
        </div>
      )}
      <div className="space-y-2">
        {data.messages.map((msg) => (
          <div
            key={msg.id}
            className={`rounded p-3 text-sm ${
              msg.direction === "inbound"
                ? "bg-gray-100 text-gray-900"
                : "bg-indigo-50 text-indigo-900 ml-8"
            }`}
          >
            <p className="text-xs text-gray-400 mb-1">
              {msg.direction === "inbound" ? "User" : "Dovebot"} —{" "}
              {msg.sent_at.slice(0, 16).replace("T", " ")}
            </p>
            <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
          </div>
        ))}
        {data.messages.length === 0 && (
          <p className="text-sm text-gray-400">No messages yet.</p>
        )}
      </div>
    </div>
  );
};

const EmailThreads = () => {
  const [phaseFilter, setPhaseFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["admin-email-threads", phaseFilter],
    queryFn: () =>
      getAdminEmailThreads(phaseFilter === "all" ? undefined : phaseFilter),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Email Threads</h1>
        <Select value={phaseFilter} onValueChange={setPhaseFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All phases</SelectItem>
            <SelectItem value="inquiry">Inquiry</SelectItem>
            <SelectItem value="awaiting_shift">Awaiting shift</SelectItem>
            <SelectItem value="shift">Shift</SelectItem>
            <SelectItem value="change">Change</SelectItem>
            <SelectItem value="dormant">Dormant</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-gray-500">Loading...</p>}

      <div className="space-y-3">
        {threads.map((t) => (
          <div key={t.id} className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="font-medium text-gray-900">{t.user_email}</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {t.persona_name.replace(/_/g, ' ')}
                  </span>
                  <Badge variant={phaseVariant[t.phase] ?? "outline"}>
                    {t.phase}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">
                  {t.last_reply_at
                    ? t.last_reply_at.slice(0, 10)
                    : t.created_at.slice(0, 10)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setExpandedId(expandedId === t.id ? null : t.id)
                  }
                >
                  {expandedId === t.id ? "Hide" : "View"}
                </Button>
              </div>
            </div>
            {expandedId === t.id && <ThreadDetail threadId={t.id} />}
          </div>
        ))}
        {!isLoading && threads.length === 0 && (
          <p className="text-gray-500">No threads found.</p>
        )}
      </div>
    </div>
  );
};

export default EmailThreads;
